(() => {
  const scripts = new Map();
  function script(src) {
    if (!scripts.has(src)) scripts.set(src, new Promise((resolve, reject) => {
      const tag = document.createElement('script'); tag.src = src; tag.async = true;
      tag.onload = resolve; tag.onerror = () => { scripts.delete(src); tag.remove(); reject(new Error('Map library unavailable')); };
      document.head.append(tag);
    }));
    return scripts.get(src);
  }
  class PastoreMapCompare extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.provider = 'maptiler'; this.maps = {}; this.loading = new Set();
      this.canvas = this.querySelector('.p-live-map');
      this.status = this.querySelector('.p-map-status');
      this.activated = false;
      this.querySelectorAll('[data-provider]').forEach(button => button.addEventListener('click', () => this.select(button.dataset.provider), {signal:this.controller.signal}));
      this.select(this.dataset.providerMode === 'compare' ? (this.dataset.maptiler ? 'maptiler' : 'google') : (this.dataset.providerMode || 'google'));
      this.observer = new IntersectionObserver(entries => {
        if(entries.some(entry => entry.isIntersecting)) {
          this.activated = true; this.observer.disconnect(); this.load();
        }
      }, {rootMargin:'600px'});
      this.observer.observe(this);

    }
    disconnectedCallback() {
      this.observer?.disconnect(); this.controller?.abort(); this.maps?.maptiler?.remove();
      if (this.maps?.google && window.google?.maps) window.google.maps.event.clearInstanceListeners(this.maps.google);
    }
    valid() { return this.dataset.lat.trim() !== '' && this.dataset.lng.trim() !== '' && Number.isFinite(+this.dataset.lat) && Number.isFinite(+this.dataset.lng) && Math.abs(+this.dataset.lat)<=90 && Math.abs(+this.dataset.lng)<=180; }
    select(provider) {
      this.provider = provider;
      this.querySelectorAll('[data-provider]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.provider === provider)));
      this.canvas.querySelectorAll('[data-map]').forEach(el => { el.hidden = el.dataset.map !== provider; });
      const ready = !!this.dataset[provider] && this.valid();
      const name = provider === 'maptiler' ? 'MapTiler' : 'Google Maps';
      this.status.textContent = this.maps[provider] ? name + ' interactive preview' : ready ? 'Loading map…' : 'Map unavailable. Use the directions buttons above.';
      if(this.maps[provider]) requestAnimationFrame(() => { if(provider==='maptiler') this.maps[provider].resize(); else google.maps.event.trigger(this.maps[provider],'resize'); });
      if(this.activated && ready && !this.maps[provider]) this.load();
    }
    async load() {
      const provider=this.provider; if (!this.dataset[provider] || !this.valid() || this.loading.has(provider) || this.maps[provider]) return;
      this.loading.add(provider);
      this.status.textContent='Loading map…';
      const host=document.createElement('div'); host.dataset.map=provider; host.style.cssText='height:100%;width:100%'; this.canvas.append(host);
      try {
        const center={lat:+this.dataset.lat,lng:+this.dataset.lng}, zoom=+this.dataset.zoom;
        if(provider==='maptiler') {
          if(!document.querySelector('[data-pastore-map-css]')) {
            const css=document.createElement('link'); css.rel='stylesheet'; css.href='https://cdn.maptiler.com/maptiler-sdk-js/v4.2.0/maptiler-sdk.css'; css.dataset.pastoreMapCss='true'; document.head.append(css);
          }
          await script('https://cdn.maptiler.com/maptiler-sdk-js/v4.2.0/maptiler-sdk.umd.min.js');
          if(!this.isConnected) return;
          const sdk=window.maptilersdk;
          // Local web fonts keep map labels consistent with the illustrated homepage.
          let style=this.dataset.style;
          if(!style) {
            await Promise.all([['Pastore Map Heading',this.dataset.headingFont],['Pastore Map Body',this.dataset.bodyFont]].map(async ([name,url])=>{
              const face=await new FontFace(name,'url('+JSON.stringify(url)+')').load(); document.fonts.add(face);
            }));
            const response=await fetch('https://api.maptiler.com/maps/streets-v2/style.json?key='+encodeURIComponent(this.dataset.maptiler));
            if(!response.ok) throw new Error('Map style unavailable');
            style=await response.json();
            delete style.glyphs;
            style.layers.forEach(layer=>{
              if(layer.type==='symbol' && layer.layout?.['text-field']) {
                const heading=/place|settlement|town|city|country|continent|state|river|ocean|lake/i.test(layer.id);
                layer.layout['text-font']=[heading?'Pastore Map Heading':'Pastore Map Body'];
                if(heading) {layer.layout['text-size']={stops:[[8,14],[12,20],[16,24]]}; layer.layout['text-transform']='uppercase'; layer.layout['text-letter-spacing']=0.04;}
              }
            });
          }
          const map=new sdk.Map({container:host,apiKey:this.dataset.maptiler,style,center:[center.lng,center.lat],zoom:zoom-1,geolocateControl:false});
          this.maps[provider]=map;
          map.scrollZoom.disable();
          map.on('load',()=>{
            if(!this.dataset.style) map.getStyle().layers.forEach(layer=>{
              const id=layer.id.toLowerCase();
              if(layer.type==='background') map.setPaintProperty(layer.id,'background-color','#e8e8e6');
              else if(layer.type==='fill' && /water/.test(id)) map.setPaintProperty(layer.id,'fill-color','#40a8e8');
              else if(layer.type==='fill') { map.setPaintProperty(layer.id,'fill-color',/building|residential|commercial|industrial/.test(id)?'#cececb':'#e8e8e6'); if(layer.paint?.['fill-pattern']) map.setPaintProperty(layer.id,'fill-pattern',null); }
              else if(layer.type==='line' && /water|river|aqueduct/.test(id)) {
                map.setPaintProperty(layer.id,'line-color','#40a8e8');
                // Keep channels delicate; the river's water polygons retain their true width.
                map.setPaintProperty(layer.id,'line-width',{stops:[[8,0.4],[12,0.8],[16,1.5],[20,2]]});
              }
              else if(layer.type==='line' && /rail/.test(id)) {
                if(/hatch|dash/.test(id)) {map.setLayoutProperty(layer.id,'visibility','none');}
                else {
                  map.setPaintProperty(layer.id,'line-color','#ffb000');
                  map.setPaintProperty(layer.id,'line-width',{stops:[[8,1],[12,3],[16,6]]});
                  const outline=JSON.parse(JSON.stringify(layer)); outline.id=layer.id+'-pastore-outline';
                  outline.paint={...outline.paint,'line-color':'#24243f','line-width':{stops:[[8,2],[12,5],[16,9]]}};
                  map.addLayer(outline,layer.id);
                }
              }
              else if(layer.type==='line' && /road|motorway|street|path|bridge/.test(id)) map.setPaintProperty(layer.id,'line-color',/casing/.test(id)?'#cececb':'#ffffff');
              else if(layer.type==='symbol' && layer.layout?.['text-field']) {
                map.setPaintProperty(layer.id,'text-color','#24243f');
                map.setPaintProperty(layer.id,'text-halo-color','#e8e8e6');
                map.setPaintProperty(layer.id,'text-halo-width',1.5);
              }
            });
          });
          map.on('error',(event)=>{if(event.error?.message==='Cannot remove non-existing layer \"Halo Layer\".') return; this.status.textContent='MapTiler could not load some map data. Check the API key, domain restrictions and allowance.';});
          const pin=document.createElement('div'); pin.className='p-map-marker p-map-marker--illustrated'; const logo=document.createElement('img'); logo.src=this.dataset.crook; logo.alt=''; logo.width=40; logo.height=40; pin.append(logo); pin.setAttribute('aria-label','Pastore Brewing & Blending');
          new sdk.Marker({element:pin}).setLngLat([center.lng,center.lat]).setPopup(new sdk.Popup().setText('Pastore Brewing & Blending')).addTo(map);
        } else {
          await script('https://maps.googleapis.com/maps/api/js?key='+encodeURIComponent(this.dataset.google)+'&v=quarterly');
          if(!this.isConnected) return;
          const {Map}=await google.maps.importLibrary('maps');
          const map=new Map(host,{center,zoom,gestureHandling:'cooperative',mapTypeControl:false,streetViewControl:false,styles:[
            {elementType:'geometry',stylers:[{color:'#e8e8e6'}]},
            {elementType:'labels.text.fill',stylers:[{color:'#24243f'}]},
            {elementType:'labels.text.stroke',stylers:[{color:'#e8e8e6'},{weight:2}]},
            {elementType:'labels.icon',stylers:[{visibility:'off'}]},
            {featureType:'poi',stylers:[{visibility:'off'}]},
            {featureType:'landscape.natural',elementType:'geometry.fill',stylers:[{color:'#e8e8e6'}]},
            {featureType:'landscape.man_made',elementType:'geometry.fill',stylers:[{color:'#cececb'}]},
            {featureType:'administrative',elementType:'geometry.stroke',stylers:[{color:'#cececb'}]},
            {featureType:'road',elementType:'geometry.fill',stylers:[{color:'#ffffff'}]},
            {featureType:'road',elementType:'geometry.stroke',stylers:[{color:'#cececb'},{weight:0.5}]},
            {featureType:'road.highway',elementType:'geometry.fill',stylers:[{color:'#ffffff'}]},
            {featureType:'road.highway',elementType:'geometry.stroke',stylers:[{color:'#cececb'},{weight:1}]},
            {featureType:'transit.line',elementType:'geometry.fill',stylers:[{color:'#ffb000'}]},
            {featureType:'transit.line',elementType:'geometry.stroke',stylers:[{color:'#24243f'},{weight:1}]},
            {featureType:'transit.station.rail',elementType:'labels',stylers:[{visibility:'on'}]},
            {featureType:'water',elementType:'geometry.fill',stylers:[{color:'#40a8e8'}]},
            {featureType:'water',elementType:'labels.text.fill',stylers:[{color:'#24243f'}]}
          ]}); this.maps[provider]=map;
          class Pin extends google.maps.OverlayView {
            onAdd(){this.el=document.createElement('div');this.el.className='p-map-marker p-map-marker--illustrated';const logo=document.createElement('img'); logo.src=this.crook; logo.alt=''; logo.width=40; logo.height=40;this.el.append(logo);this.el.title='Pastore Brewing & Blending';this.el.style.position='absolute';this.getPanes().overlayMouseTarget.append(this.el);}
            draw(){const point=this.getProjection().fromLatLngToDivPixel(new google.maps.LatLng(center));this.el.style.left=(point.x-32)+'px';this.el.style.top=(point.y-32)+'px';}
            onRemove(){this.el.remove();}
          }
          const pin=new Pin();pin.crook=this.dataset.crook;pin.setMap(map);
        }
        this.loading.delete(provider); this.select(this.provider);
      } catch (_) {this.loading.delete(provider); host.remove();this.status.textContent='The map could not load. Please check the account settings or use the directions link.';this.status.classList.add('p-map-status--error');}
    }
  }
  if(!customElements.get('pastore-map-compare')) customElements.define('pastore-map-compare',PastoreMapCompare);
})();